import React from 'react';
import { motion } from 'framer-motion';
import { MessageCircleIcon, PhoneIcon, ArrowRightIcon } from 'lucide-react';
import { useLanguage } from '../hooks/useLanguage';
import { WhatsAppIcon } from './WhatsAppIcon';
export function Hero() {
  const {
    language
  } = useLanguage();
  const content = {
    en: {
      h1: 'Private Driver, Airport Transfers and Villa Transport in Ibiza',
      subheadline: 'Fixed-price private transfers across Ibiza for airport pickups, villa arrivals, restaurants, beach clubs and group travel. Fast WhatsApp booking with local drivers.',
      priceAnchor: 'Most bookings between €100 and €120 depending on the area.',
      primaryCta: 'Get your price on WhatsApp',
      secondaryCta: 'See airport prices',
      trustLine: 'From €120 · Instant reply · Fixed price · Local drivers'
    },
    es: {
      h1: 'Chófer Privado, Transfers Aeropuerto y Transporte a Villas en Ibiza',
      subheadline: 'Transfers privados a precio fijo en Ibiza para aeropuerto, villas, restaurantes, beach clubs y grupos. Reserva rápida por WhatsApp con conductores locales.',
      priceAnchor: 'La mayoría de reservas entre €100 y €120 según la zona.',
      primaryCta: 'Tu precio por WhatsApp',
      secondaryCta: 'Ver precios aeropuerto',
      trustLine: 'Desde €120 · Respuesta inmediata · Precio fijo · Conductores locales'
    }
  };
  const text = content[language];
  const whatsappUrl = language === 'en' ? 'https://wa.me/34613756211?text=Hello%2C%20I%20need%20a%20transfer%20in%20Ibiza.%0APickup%3A%0ADestination%3A%0ADate%3A%0ATime%3A%0APassengers%3A' : 'https://wa.me/34613756211?text=Hola%2C%20necesito%20un%20transfer%20en%20Ibiza.%0ARecogida%3A%0ADestino%3A%0AFecha%3A%0AHora%3A%0APasajeros%3A';
  const heroImage = 'https://cdn.magicpatterns.com/uploads/8oYYLEAaJU1SLe3Q7VC128/foto_2.jpg';
  return <section id="home" className="relative bg-gradient-to-b from-navy to-navy-light overflow-hidden" data-id="element-115">
      <div className="mx-auto max-w-7xl px-5" data-id="element-116">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-center pt-24 pb-8 md:pt-40 md:pb-24 lg:pt-44 lg:pb-28 lg:min-h-[88vh]" data-id="element-117">
          {/* Left — Text content */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="lg:col-span-7 flex flex-col justify-center" data-id="element-118">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading text-white leading-[1.1] mb-6" data-id="element-119">
              {text.h1}
            </h1>

            <p className="text-lg md:text-xl text-white/80 font-light leading-relaxed mb-4 max-w-2xl" data-id="element-120">
              {text.subheadline}
            </p>

            <p className="text-sm text-gold-subtle/80 font-medium mb-8" data-id="element-121">
              {text.priceAnchor}
            </p>

            {/* Mobile Image */}
            <div className="block lg:hidden w-full mb-8 rounded-2xl overflow-hidden shadow-2xl relative aspect-[5/2]" data-id="element-122">
              <img src={heroImage} alt="Premium private transfer in Ibiza" className="w-full h-full object-cover" data-id="element-123" />
              <div className="absolute inset-0 bg-gradient-to-t from-navy/60 to-transparent" data-id="element-124"></div>
            </div>

            <div className="flex flex-col sm:flex-row items-center gap-4 mb-8" data-id="element-125">
              <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center space-x-2 bg-whatsapp hover:bg-whatsapp-hover text-white px-8 py-4 rounded-full font-medium transition-all duration-300 shadow-lg shadow-whatsapp/20 hover:shadow-whatsapp/40 hover:-translate-y-0.5" data-id="element-126">
                <WhatsAppIcon className="w-4 h-4 md:w-5 md:h-5" data-id="element-127" />
                <span data-id="element-128">{text.primaryCta}</span>
              </a>
              <a href="#pricing" className="w-full sm:w-auto flex items-center justify-center space-x-2 text-white/70 hover:text-gold-subtle px-5 py-3 text-sm font-medium transition-colors" data-id="element-129">
                <span data-id="element-130">{text.secondaryCta}</span>
                <ArrowRightIcon className="w-4 h-4" data-id="element-131" />
              </a>
            </div>

            <div className="flex items-center space-x-2 text-sm text-white/60 font-medium" data-id="element-132">
              <span data-id="element-133">{text.trustLine}</span>
            </div>
          </motion.div>

          {/* Right — Desktop Image */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="hidden lg:block lg:col-span-5 relative" data-id="element-134">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl aspect-[4/5]" data-id="element-135">
              <img src={heroImage} alt="Premium private transfer in Ibiza" className="w-full h-full object-cover" data-id="element-136" />
              <div className="absolute inset-0 bg-gradient-to-t from-navy/80 via-navy/20 to-transparent" data-id="element-137"></div>
            </div>

            {/* Decorative elements */}
            <div className="absolute -bottom-6 -left-6 w-24 h-24 bg-gold-subtle/20 rounded-full blur-2xl" data-id="element-138"></div>
            <div className="absolute -top-6 -right-6 w-32 h-32 bg-whatsapp/20 rounded-full blur-3xl" data-id="element-139"></div>
          </motion.div>
        </div>
      </div>
    </section>;
}